import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { RCCGrid, RCCRefModalProps} from "react-common-components";
import { useForm } from "react-hook-form";
import ComposedChart from "src/components/Charts/ComposedChart";
import List from "src/components/List";
import Modal from "src/components/Modal";
import Select from "src/components/Select";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { IIntbb001BudgetGrouper, IChartYearGrouper, IRefActionsModalBudget } from "../interface";

const ModalBudget: React.ForwardRefRenderFunction<IRefActionsModalBudget> = (_, ref) => {
	const { translate } = useTranslation();
		
	// Modal Open Budget
	const modalOpenBudget = useRef<RCCRefModalProps>(null);
	const [subtitle, setSubtitle] = useState<string>("");
	// Charts data
	const [fulldataChartBudget, setFullDataChartBudget] = useState<IIntbb001BudgetGrouper[]>([]);
	const [dataChartBudget, setDataChartBudget] = useState<IIntbb001BudgetGrouper[]>([]);
	const [dataYearChartBudget, setDataYearChartBudget] = useState<IChartYearGrouper[]>([]);
	// Table
	const [dataTableBudget, setDataTableBudget] = useState<IIntbb001BudgetGrouper[]>([]);
	// Select
	const [selectYearOptions, setSelectYearOptions] = useState<{label: string, value: string}[]>([]);
	const [selectedYearOption, setSelectedYearOption] = useState<string>("");
	const { control: controlChartBudget} = useForm();
	const actionButtonOpenBudget = (grouperTitle: string, data: IIntbb001BudgetGrouper[]) => { 
		setSubtitle(grouperTitle);
		const setYears = Array.from(new Set(data.map((dt) => dt.year.toString())));
		const newOptions = setYears.map((years: string) => ({
			value: years,
			label: years
		}));
		
		setSelectYearOptions(newOptions);
		setSelectedYearOption(new Date().getFullYear().toString());
		setFullDataChartBudget(data);
		const yearChart: IChartYearGrouper[] = setYears.map((years: string) => {
			const dt = data.filter((x) => x.year === years);
			let totalValueExpense = 0;
			let totalValueRealizedExpense = 0;
			let totalValueRealizedRevenue = 0;
			let totalValueReleased = 0;
			let totalValueRevenue = 0;
			for (let i = 0; i < dt.length; i += 1) {
				totalValueExpense += dt[i].valueExpense;
				totalValueRealizedExpense += dt[i].valueRealizedExpense;
				totalValueRealizedRevenue += dt[i].valueRealizedRevenue;
				totalValueReleased += dt[i].valueReleased;
				totalValueRevenue += dt[i].valueRevenue;
			}
			return {
				name: years,
				valueExpense: totalValueExpense,
				valueRealizedExpense: totalValueRealizedExpense,
				valueRealizedRevenue: totalValueRealizedRevenue,
				valueReleased: totalValueReleased,
				valueRevenue: totalValueRevenue
			};
		});
		setDataYearChartBudget(yearChart);
		modalOpenBudget.current?.openModal();
	};
	useEffect(() => {
		const data = fulldataChartBudget.filter((x) => x.year === selectedYearOption);
		setDataChartBudget(data);
		const total = dataYearChartBudget.find((x) => x.name === selectedYearOption);
		if(total)
			setDataTableBudget([...data, {
				name: translate("Total"),
				valueExpense: total.valueExpense,
				valueRealizedExpense: total.valueRealizedExpense,
				valueRealizedRevenue: total.valueRealizedRevenue,
				valueReleased: total.valueReleased,
				valueRevenue: total.valueRevenue,
				year: selectedYearOption
			}]);

	}, [fulldataChartBudget, dataYearChartBudget, selectedYearOption]);
	const changeYearChart = (e: { value: string, label: string}) => {
		setSelectedYearOption(e.value);
	};
	const MemorizedChart = useMemo(() => <ComposedChart 
		width={500}
		height={400}
		data={dataChartBudget}
		xAxis={{
			scale: "band",
			label:{ value: translate("Mês"), position: "insideBottom", offset: 0 }
		}}
		yAxis={[{
			label:{ value: " ", position: "insideLeft", offset: 0, angle: -90 }
		}
		]}
		bar={[{
			dataKey: "valueRealizedExpense",
			barSize: 20,
			name: translate("Valor realizado despesa")
		},{
			dataKey: "valueRealizedRevenue",
			barSize: 20,
			name: translate("Valor realizado receita")
		},{
			dataKey: "valueReleased",
			barSize: 20,
			name: translate("Valor liberado")  
		}]}
		line={[{
			dataKey: "valueExpense",
			type: "monotone",
			name: translate("Valor previsto despesa")  
		},
		{
			dataKey: "valueRevenue",
			type: "monotone",
			name: translate("Valor previsto receita") 
		}]}
	/>, [dataChartBudget]);
	const MemorizedChartYear = useMemo(() => <ComposedChart 
		width={500}
		height={400}
		data={dataYearChartBudget}
		xAxis={{
			scale: "band",
			label:{ value: translate("Ano"), position: "insideBottom", offset: 0 }
		}}
		yAxis={[{
			label:{ value: " ", position: "insideLeft", offset: 0, angle: -90 }
		}]}
		bar={[{
			dataKey: "valueRealizedExpense",
			barSize: 20,
			name: translate("Valor realizado despesa")
		},{
			dataKey: "valueRealizedRevenue",
			barSize: 20,
			name: translate("Valor realizado receita")
		},{
			dataKey: "valueReleased",
			barSize: 20,
			name: translate("Valor liberado")  
		}]}
		line={[{
			dataKey: "valueExpense",
			type: "monotone",
			name: translate("Valor previsto despesa")  
		},
		{
			dataKey: "valueRevenue",
			type: "monotone",
			name: translate("Valor previsto receita") 
		}]}
		styleInline=".recharts-legend-wrapper{ bottom: -10px !important; }"
	/>, [dataYearChartBudget]);
	const MemorizedTable = useMemo(() => <List 
		layout="table"
		data={dataTableBudget}
		columns={[{
			idColumn: "name",
			nameColumn: translate("Mês"),
			xs: 12,
			sm: 2,
			justify: "left",
			truncate: true
		},{
			idColumn: "valueExpense",
			nameColumn: translate("Valor previsto despesa"),
			xs: 12,
			sm: 2,
			justify: "left",
			type: "number"
		},{
			idColumn: "valueRealizedExpense",
			nameColumn: translate("Valor realizado despesa"),
			xs: 12,
			sm: 2,
			justify: "left",
			type: "number"
		},{
			idColumn: "valueRevenue",
			nameColumn: translate("Valor previsto receita"),
			xs: 12,
			sm: 2,
			justify: "left",
			type: "number"
		},
		{
			idColumn: "valueRealizedRevenue",
			nameColumn: translate("Valor realizado receita"),
			xs: 12,
			sm: 2,
			justify: "left",
			type: "number"
		},
		{
			idColumn: "valueReleased",
			nameColumn: translate("Valor liberado"),
			xs: 12,
			sm: 2,
			justify: "left",
			type: "number"
		}]}
		showHeader
	/>, [dataTableBudget]);

	useImperativeHandle(ref, () => ({
		actionButtonOpenBudget
	}));
    
	return (
		<>		
			{/* Modal CHART BUDGET */}
			<Modal
				ref={modalOpenBudget}
				header={{
					title: translate("Orçamento"),
					icon: {
						name: "dollar"
					},
					subtitle
				}}
				footer= {{
					showSaveButton: false,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar")
				}}
				styleInline="width: 95vw;"
			>
				<RCCGrid container style={{position: "relative"}}>
					<RCCGrid
						container
						item
						justifyContent="center"
						sm={12}
						md={8}
					>
						<Title size="md" color="purple">{translate("Valores mensais")}</Title>
					</RCCGrid>
					<RCCGrid
						container
						item
						justifyContent="center"
						sm={12}
						md={4}
					>
						<Title size="md" color="purple">{translate("Valores anuais")}</Title>
					</RCCGrid>
					<div style={{position: "absolute", top: "-24px", right: "12px", width: "80px"}}>
						<Select 
							control={controlChartBudget}
							name="year"
							label=""
							defaultValue={new Date().getFullYear().toString()}
							options={selectYearOptions}
							onChangeCapture={changeYearChart}
						/>
					</div>
				</RCCGrid>
				<RCCGrid container style={{minHeight: "60%"}}>
					<RCCGrid
						container
						item
						sm={12}
						md={8}
					>
						{MemorizedChart}
					</RCCGrid>
					<RCCGrid
						container
						item
						sm={12}
						md={4}
					>
						{MemorizedChartYear}
					</RCCGrid>
				</RCCGrid>

				<RCCGrid container style={{marginTop: "40px"}}>
					{MemorizedTable}
				</RCCGrid>		
			</Modal>
		</>
	);
};

export default forwardRef(ModalBudget);
